import { addDays, weekdayOf, type ISODate } from './format'

export interface CalendarLike { workingWeekdays: number[]; exceptions: { fromDate: ISODate; toDate: ISODate; working: boolean }[] }

/** Exceptions apply in order; a later one wins (matches the engine). */
export const isWorkingDay = (cal: CalendarLike, iso: ISODate): boolean => {
  let working = cal.workingWeekdays.includes(weekdayOf(iso))
  for (const e of cal.exceptions) if (iso >= e.fromDate && iso <= e.toDate) working = e.working
  return working
}

/** Inclusive count of working days in [from, to]; 0 when to < from. */
export const workingDaysBetween = (cal: CalendarLike, from: ISODate, to: ISODate): number => {
  let n = 0
  for (let d = from; d <= to; d = addDays(d, 1)) if (isWorkingDay(cal, d)) n++
  return n
}

/** The date `n` working days after `iso` (n ≥ 1), counting `iso` itself when it is working and n === 0 → iso. */
export const addWorkingDays = (cal: CalendarLike, iso: ISODate, n: number): ISODate => {
  let d = iso
  let left = n
  while (left > 0) {
    d = addDays(d, 1)
    if (isWorkingDay(cal, d)) left--
  }
  return d
}

export const calendarFor = (activity: { calendarId: string | null }, calendarsById: Map<string, CalendarLike>, defaultCalendarId: string): CalendarLike | null =>
  calendarsById.get(activity.calendarId ?? defaultCalendarId) ?? null

/**
 * Review fix (Important #3): signed working-day variance between a baseline finish and the
 * current finish, on `cal` — matches the backend's `WorkingCalendar.distance(a, b)`
 * (`src/services/schedule/engine/calendar.ts`, `ceil(b) - ceil(a)`), which is what
 * `ScheduleStats.finishVarianceDays` reports. Positive when `finish` is later than
 * `baselineFinish` (a slip), negative when earlier, 0 when equal — same sign convention the
 * tooltip/panel already used for the calendar-day `diffDays` this replaces. Each date is first
 * snapped forward to the next working day (mirrors `ceil`) so the result stays correct even when
 * either lands on a non-working day.
 */
export const finishVarianceWorkingDays = (cal: CalendarLike, baselineFinish: ISODate, finish: ISODate): number => {
  const ceilToWorkingDay = (iso: ISODate): ISODate => { let d = iso; while (!isWorkingDay(cal, d)) d = addDays(d, 1); return d }
  const a = ceilToWorkingDay(baselineFinish)
  const b = ceilToWorkingDay(finish)
  if (a === b) return 0
  return b > a ? workingDaysBetween(cal, a, b) - 1 : -(workingDaysBetween(cal, b, a) - 1)
}
